import { useEffect, useState } from "react";
import { api } from "../api/axios";
import { useI18n } from "../../i18n/I18nContext";

function VariantTypeBadge({ isNext }) {
  const { t } = useI18n();

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${
        isNext
          ? "border-blue-200 bg-blue-50 text-blue-700"
          : "border-purple-200 bg-purple-50 text-purple-700"
      }`}
    >
      {isNext ? t("Seguinte") : t("Base")}
    </span>
  );
}

function formatMoney(v, currency = "EUR") {
  if (v === null || v === undefined || v === "") return "—";
  const num = Number(v);
  if (Number.isNaN(num)) return String(v);

  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency,
  }).format(num);
}

function formatMeasure(v, unit = "") {
  if (v === null || v === undefined || v === "") return "—";
  return `${v} ${unit}`.trim();
}

const emptyVariantForm = {
  sku: "",
  price: "",
  currency: "EUR",
  is_active: true,
  is_next: false,
  height_cm: "",
  width_cm: "",
  depth_cm: "",
  weight_kg: "",
  load_kg: "",
  levels: "",
};

function mapVariantToForm(variant) {
  return {
    sku: variant?.sku ?? "",
    price: variant?.price ?? "",
    currency: variant?.currency ?? "EUR",
    is_active: !!variant?.is_active,
    is_next: !!variant?.is_next,
    height_cm: variant?.height_cm ?? "",
    width_cm: variant?.width_cm ?? "",
    depth_cm: variant?.depth_cm ?? "",
    weight_kg: variant?.weight_kg ?? "",
    load_kg: variant?.load_kg ?? "",
    levels: variant?.levels ?? "",
  };
}

export default function AdminProductVariants({ productId, productName }) {
  const { t } = useI18n();
  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 4,
    total: 0,
    from: null,
    to: null,
  });

    const [filters, setFilters] = useState({
        sku: "",
        height_cm: "",
        width_cm: "",
    });

  const [newVariant, setNewVariant] = useState({ ...emptyVariantForm });

  const [editingId, setEditingId] = useState(null);
  const [editVariant, setEditVariant] = useState({ ...emptyVariantForm });

  const getFirstLaravelError = (err) => {
    const data = err?.response?.data;

    if (data?.errors) {
      const firstKey = Object.keys(data.errors)[0];
      return data.errors[firstKey]?.[0] || t("Erro de validação.");
    }

    return data?.message || t("Erro inesperado.");
  };

  const toNullableNumber = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  };

  const buildVariantPayload = (variantForm) => ({
    sku: variantForm.sku?.trim() || null,
    price: Number(variantForm.price),
    currency: variantForm.currency || "EUR",
    is_active: variantForm.is_active ? 1 : 0,
    is_next: variantForm.is_next ? 1 : 0,
    height_cm: Number(variantForm.height_cm),
    width_cm: Number(variantForm.width_cm),
    depth_cm: toNullableNumber(variantForm.depth_cm),
    weight_kg: Number(variantForm.weight_kg),
    load_kg: toNullableNumber(variantForm.load_kg),
    levels: toNullableNumber(variantForm.levels),
  });

  const validateVariantForm = (variantForm) => {
    if (variantForm.price === "" || Number.isNaN(Number(variantForm.price))) {
      return t("O preço da variante é obrigatório.");
    }

    if (variantForm.height_cm === "" || Number.isNaN(Number(variantForm.height_cm))) {
      return t("A altura é obrigatória.");
    }

    if (variantForm.width_cm === "" || Number.isNaN(Number(variantForm.width_cm))) {
      return t("A largura é obrigatória.");
    }

    if (variantForm.weight_kg === "" || Number.isNaN(Number(variantForm.weight_kg))) {
      return t("O peso é obrigatório.");
    }

    return "";
  };

  const loadVariants = async (pageToLoad = 1, customFilters = filters) => {
    setVariantsLoading(true);
    setErrorMsg("");

    try {
            const params = {
            page: pageToLoad,
            per_page: 4,
            };

            if (customFilters.sku?.trim()) {
            params.sku = customFilters.sku.trim();
            }

            if (customFilters.height_cm !== "") {
            params.height_cm = customFilters.height_cm;
            }

            if (customFilters.width_cm !== "") {
            params.width_cm = customFilters.width_cm;
            }

            const res = await api.get(`/api/admin/products/${productId}/variants`, {
            params,
            });

            setVariants(res.data?.data ?? []);
            setMeta({
            current_page: res.data?.meta?.current_page ?? 1,
            last_page: res.data?.meta?.last_page ?? 1,
            per_page: res.data?.meta?.per_page ?? 4,
            total: res.data?.meta?.total ?? 0,
            from: res.data?.meta?.from ?? null,
            to: res.data?.meta?.to ?? null,
        });
        } catch (err) {
            setErrorMsg(err?.response?.data?.message || t("Erro ao carregar variantes."));
        } finally {
            setVariantsLoading(false);
        }
    };

  useEffect(() => {
    loadVariants(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, productId]);

  const applyFilters = async () => {
    setPage(1);
    await loadVariants(1, filters);
  };

  const clearFilters = async () => {
    const reset = {
        sku: "",
        height_cm: "",
        width_cm: "",
    };

    setFilters(reset);
    setPage(1);
    await loadVariants(1, reset);
    };

  const createVariant = async () => {
    const validationError = validateVariantForm(newVariant);

    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    setSaving(true);
    setErrorMsg("");

    try {
      await api.post(
        `/api/admin/products/${productId}/variants`,
        buildVariantPayload(newVariant)
      );

      setNewVariant({ ...emptyVariantForm });
      setPage(1);
      await loadVariants(1);
    } catch (err) {
      setErrorMsg(getFirstLaravelError(err));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (variant) => {
    setEditingId(variant.id);
    setEditVariant(mapVariantToForm(variant));
    setErrorMsg("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditVariant({ ...emptyVariantForm });
  };

  const saveEdit = async (variantId) => {
    const validationError = validateVariantForm(editVariant);

    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    setSaving(true);
    setErrorMsg("");

    try {
      await api.put(
        `/api/admin/products/${productId}/variants/${variantId}`,
        buildVariantPayload(editVariant)
      );

      setEditingId(null);
      setEditVariant({ ...emptyVariantForm });
      await loadVariants(page);
    } catch (err) {
      setErrorMsg(getFirstLaravelError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteVariant = async (variantId) => {
    if (!window.confirm(t("Queres mesmo apagar esta variante?"))) return;

    setSaving(true);
    setErrorMsg("");

    try {
      await api.delete(`/api/admin/products/${productId}/variants/${variantId}`);

      const nextPage = variants.length === 1 && page > 1 ? page - 1 : page;
      setPage(nextPage);
      await loadVariants(nextPage);
    } catch (err) {
      setErrorMsg(getFirstLaravelError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-5">
      <div>
        <h2 className="text-lg font-bold text-gray-900">{t("Variantes")}</h2>
        <p className="mt-1 text-sm text-gray-600">
          {t("Gestão de variantes para")}{" "}
          <span className="font-medium">{productName}</span>.
        </p>
      </div>

      {errorMsg && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMsg}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-600">
            {t("Filtrar variantes")}
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">SKU</label>
            <input
                type="text"
                placeholder={t("Ex: RACK-200")}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={filters.sku}
                onChange={(e) => setFilters((p) => ({ ...p, sku: e.target.value }))}
                onKeyDown={(e) => {
                if (e.key === "Enter") applyFilters();
                }}
            />
            </div>

            <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              {t("Altura")} (cm)
            </label>
            <input
                type="number"
                step="0.01"
                placeholder={t("Ex: 200")}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={filters.height_cm}
                onChange={(e) =>
                setFilters((p) => ({ ...p, height_cm: e.target.value }))
                }
                onKeyDown={(e) => {
                if (e.key === "Enter") applyFilters();
                }}
            />
            </div>

            <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              {t("Largura")} (cm)
            </label>
            <input
                type="number"
                step="0.01"
                placeholder={t("Ex: 120")}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={filters.width_cm}
                onChange={(e) =>
                setFilters((p) => ({ ...p, width_cm: e.target.value }))
                }
                onKeyDown={(e) => {
                if (e.key === "Enter") applyFilters();
                }}
            />
            </div>
        </div>

  <div className="mt-3 flex justify-end gap-2">
    <button
      type="button"
      onClick={clearFilters}
      className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
    >
      {t("Limpar")}
    </button>

    <button
      type="button"
      onClick={applyFilters}
      className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
    >
      {t("Filtrar")}
    </button>
  </div>
</div>

      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
          {t("Variantes atuais")}
        </div>

        <div className="max-h-[34rem] overflow-y-auto">
          {variantsLoading ? (
            <div className="px-3 py-4 text-sm text-gray-500">
              {t("A carregar variantes...")}
            </div>
          ) : variants.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-500">
              {t("Ainda não existem variantes.")}
            </div>
          ) : (
            variants.map((v) => {
              const isEditing = editingId === v.id;

              return (
                <div
                  key={v.id}
                  className="border-b px-3 py-3 last:border-b-0 hover:bg-gray-50"
                >
                  {!isEditing ? (
                    <div className="flex items-start justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="truncate font-medium text-gray-900">
                            {v.label || `${t("Variante")} #${v.id}`}
                          </div>
                          <VariantTypeBadge isNext={!!v.is_next} />
                        </div>

                        <div className="mt-1 text-xs text-gray-600">
                          {formatMoney(v.price, v.currency || "EUR")}
                          {v.sku ? ` • SKU: ${v.sku}` : ""}
                        </div>

                        <div className="mt-1 text-xs text-gray-500">
                          {v.height_cm ?? "—"} × {v.width_cm ?? "—"} × {v.depth_cm ?? "—"} cm
                          {" • "}
                          {t("Peso")}: {formatMeasure(v.weight_kg, "kg")}
                          {" • "}
                          {t("Carga")}: {formatMeasure(v.load_kg, "kg")}
                          {" • "}
                          {t("Níveis")}: {v.levels ?? "—"}
                          {" • "}
                          {t("Estado")}: {v.is_active ? t("Ativa") : t("Inativa")}
                        </div>
                      </div>

                      <div className="flex shrink-0 gap-2">
                        <button
                          type="button"
                          onClick={() => startEdit(v)}
                          className="rounded border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                          disabled={saving}
                        >
                          {t("Editar")}
                        </button>

                        <button
                          type="button"
                          onClick={() => deleteVariant(v.id)}
                          className="rounded border border-red-200 bg-white px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
                          disabled={saving}
                        >
                          {t("Apagar")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="text-sm font-semibold text-gray-900">
                        {t("Editar variante")}
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            {t("Tipo")}
                          </label>
                          <select
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                            value={editVariant.is_next ? "next" : "base"}
                            onChange={(e) =>
                              setEditVariant((p) => ({
                                ...p,
                                is_next: e.target.value === "next",
                              }))
                            }
                          >
                            <option value="base">{t("Base")}</option>
                            <option value="next">{t("Seguinte")}</option>
                          </select>
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            {t("SKU da variante")}
                          </label>
                          <input
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                            value={editVariant.sku}
                            onChange={(e) =>
                              setEditVariant((p) => ({ ...p, sku: e.target.value }))
                            }
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            {t("Preço")} *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                            value={editVariant.price}
                            onChange={(e) =>
                              setEditVariant((p) => ({ ...p, price: e.target.value }))
                            }
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            {t("Moeda")}
                          </label>
                          <select
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                            value={editVariant.currency}
                            onChange={(e) =>
                              setEditVariant((p) => ({ ...p, currency: e.target.value }))
                            }
                          >
                            <option value="EUR">EUR</option>
                            <option value="USD">USD</option>
                            <option value="GBP">GBP</option>
                          </select>
                        </div>

                        <div className="flex items-end">
                          <label className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm">
                            <input
                              type="checkbox"
                              checked={editVariant.is_active}
                              onChange={(e) =>
                                setEditVariant((p) => ({
                                  ...p,
                                  is_active: e.target.checked,
                                }))
                              }
                            />
                            {t("Variante ativa")}
                          </label>
                        </div>

                        <div />

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            {t("Altura")} (cm) *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                            value={editVariant.height_cm}
                            onChange={(e) =>
                              setEditVariant((p) => ({
                                ...p,
                                height_cm: e.target.value,
                              }))
                            }
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            {t("Largura")} (cm) *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                            value={editVariant.width_cm}
                            onChange={(e) =>
                              setEditVariant((p) => ({
                                ...p,
                                width_cm: e.target.value,
                              }))
                            }
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            {t("Profundidade")} (cm)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                            value={editVariant.depth_cm}
                            onChange={(e) =>
                              setEditVariant((p) => ({
                                ...p,
                                depth_cm: e.target.value,
                              }))
                            }
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            {t("Peso")} (kg) *
                          </label>
                          <input
                            type="number"
                            step="0.001"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                            value={editVariant.weight_kg}
                            onChange={(e) =>
                              setEditVariant((p) => ({
                                ...p,
                                weight_kg: e.target.value,
                              }))
                            }
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            {t("Carga")} (kg)
                          </label>
                          <input
                            type="number"
                            step="0.001"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                            value={editVariant.load_kg}
                            onChange={(e) =>
                              setEditVariant((p) => ({
                                ...p,
                                load_kg: e.target.value,
                              }))
                            }
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            {t("Níveis")}
                          </label>
                          <input
                            type="number"
                            min="1"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                            value={editVariant.levels}
                            onChange={(e) =>
                              setEditVariant((p) => ({
                                ...p,
                                levels: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                          disabled={saving}
                        >
                          {t("Cancelar")}
                        </button>

                        <button
                          type="button"
                          onClick={() => saveEdit(v.id)}
                          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60"
                          disabled={saving}
                        >
                          {saving ? t("A guardar…") : t("Guardar alterações")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between border-t bg-white px-3 py-3 text-sm">
          <div className="text-xs text-gray-600">
            {meta.total > 0
              ? `${t("A mostrar")} ${meta.from}–${meta.to} ${t("de")} ${meta.total}`
              : t("Sem resultados")}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={variantsLoading || meta.current_page <= 1}
              className="rounded border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t("Anterior")}
            </button>

            <span className="text-xs text-gray-600">
              {t("Página")} {meta.current_page} {t("de")} {meta.last_page}
            </span>

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
              disabled={variantsLoading || meta.current_page >= meta.last_page}
              className="rounded border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t("Seguinte")}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
          {t("Criar variante")}
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              {t("Tipo")}
            </label>
            <select
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
              value={newVariant.is_next ? "next" : "base"}
              onChange={(e) =>
                setNewVariant((p) => ({
                  ...p,
                  is_next: e.target.value === "next",
                }))
              }
            >
              <option value="base">{t("Base")}</option>
              <option value="next">{t("Seguinte")}</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              {t("SKU da variante")}
            </label>
            <input
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={newVariant.sku}
              onChange={(e) => setNewVariant((p) => ({ ...p, sku: e.target.value }))}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              {t("Preço")} *
            </label>
            <input
              type="number"
              step="0.01"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={newVariant.price}
              onChange={(e) => setNewVariant((p) => ({ ...p, price: e.target.value }))}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              {t("Moeda")}
            </label>
            <select
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
              value={newVariant.currency}
              onChange={(e) =>
                setNewVariant((p) => ({ ...p, currency: e.target.value }))
              }
            >
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
            </select>
          </div>

          <div className="flex items-end">
            <label className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={newVariant.is_active}
                onChange={(e) =>
                  setNewVariant((p) => ({ ...p, is_active: e.target.checked }))
                }
              />
              {t("Variante ativa")}
            </label>
          </div>

          <div />

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              {t("Altura")} (cm) *
            </label>
            <input
              type="number"
              step="0.01"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={newVariant.height_cm}
              onChange={(e) =>
                setNewVariant((p) => ({ ...p, height_cm: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              {t("Largura")} (cm) *
            </label>
            <input
              type="number"
              step="0.01"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={newVariant.width_cm}
              onChange={(e) =>
                setNewVariant((p) => ({ ...p, width_cm: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              {t("Profundidade")} (cm)
            </label>
            <input
              type="number"
              step="0.01"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={newVariant.depth_cm}
              onChange={(e) =>
                setNewVariant((p) => ({ ...p, depth_cm: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              {t("Peso")} (kg) *
            </label>
            <input
              type="number"
              step="0.001"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={newVariant.weight_kg}
              onChange={(e) =>
                setNewVariant((p) => ({ ...p, weight_kg: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              {t("Carga")} (kg)
            </label>
            <input
              type="number"
              step="0.001"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={newVariant.load_kg}
              onChange={(e) =>
                setNewVariant((p) => ({ ...p, load_kg: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              {t("Níveis")}
            </label>
            <input
              type="number"
              min="1"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={newVariant.levels}
              onChange={(e) =>
                setNewVariant((p) => ({ ...p, levels: e.target.value }))
              }
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={createVariant}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60"
            disabled={saving}
          >
            {saving ? t("A guardar…") : t("Criar variante")}
          </button>
        </div>
      </div>
    </div>
  );
}